import { useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import moment from "moment-timezone";
import { Paginator } from "primereact/paginator";
import { parse, stringify } from "query-string";
import { useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  from,
  interval,
  map,
  mapTo,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

import { Authorization } from "../../../common/AuthorizationCpt";
import { HTTP_METHODS } from "../../../utils/constants";
import { dateToRegularFormat } from "../../../utils/dates";
import { networkBackendRequest } from "../../../utils/networkUtils";
import { formatMoney } from "../../../utils/numbers";
import { CurrencyContext } from "../../CurrencyContext/CurrencyContext";
import { ExportButton } from "../../IconButtons";
import {
  ColoredTag,
  InvoiceContainer,
  VolanteId,
} from "../../Invoice/InvoiceStyles";
import { InvoicesContainer, TableHeader } from "../../Invoices/InvoicesStyles";
import PaymentLoader from "../../PaymentLoader/PaymentLoader";
import StandaloneMessage from "../../StandaloneMessage/StandaloneMessage";
import { paymentConvertResponse, POLLING_INTERVAL } from "../common";
import { StatusSectionDetails } from "../common/PaymentTableStatus";
import { getPaymentTableHeaders, ITEMS_PER_PAGE } from "../common/utils";
import { Container } from "../PaymentStyles";

export const PaymentPaidTable = (props) => {
  const { params$ } = props;

  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;

  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  const loadInvoices: any = useAsyncExecutor2(
    (params) => {
      const destroy$ = destroy$Ref.current;
      return interval(POLLING_INTERVAL).pipe(
        takeUntil(destroy$),
        startWith(params),
        mapTo(params),
        switchMap((params) => {
          const request = networkBackendRequest(
            "/quotations/paid/intranet/",
            HTTP_METHODS.GET,
            params,
            undefined
          )
            .then((response) => response.json())
            .then(paymentConvertResponse);

          return from(request);
        })
      );
    },
    {
      params$: params$.pipe(
        takeUntil(destroy$Ref.current),
        map((params: any) => ({
          ...params,
          from_date: format(params.from_date, "yyyy-MM-dd"),
          to_date: format(params.to_date, "yyyy-MM-dd"),
          order: "descending",
          page: params.page ?? 1,
          page_size: ITEMS_PER_PAGE,
          payment_status_list: "paid",
        }))
      ),
    }
  );

  const [useDollars] = useContext(CurrencyContext);
  const titles = getPaymentTableHeaders(useDollars);

  const history = useHistory();

  const gridTemplateColumns = "1fr 15rem 15rem 1fr 1fr 1fr 15rem 12rem";

  const handleDownload = (objectName) => {
    generateDownloadUrl(objectName).then((url) => {
      window.open(url, "_blank");
    });
  };

  return (
    <>
      <InvoicesContainer gridTemplateColumns={gridTemplateColumns}>
        {loadInvoices.status === "PROCESSING" && (
          <>
            <PaymentLoader numItems={ITEMS_PER_PAGE} />
          </>
        )}
        {loadInvoices.status === "SUCCESS" && (
          <>
            {loadInvoices.data.details.length === 0 && (
              <StandaloneMessage>
                No hay ningún volante facturado.
              </StandaloneMessage>
            )}

            {loadInvoices.data.details.length > 0 && (
              <>
                <TableHeader className="item-container">
                  <div className="main-info-header">{titles.importer}</div>
                  <div>{titles.status}</div>
                  <div>{titles.retirement}</div>
                  <div className="main-info-header">{titles.rucId}</div>
                  <div>{"NRO PEDIDO"}</div>
                  <div>{titles.electronicFacNum}</div>
                  <div>{titles.amount}</div>
                  <div>{titles.exportPdf}</div>
                </TableHeader>
                {loadInvoices.data.details.map((invoice) => {
                  const {
                    consigneeRuc,
                    importer,
                    volanteId,
                    withdrawalDate,
                    ruc,
                    amount,
                    electronicFacNum,
                    quotationDoc,
                  } = invoice;
                  const today = moment(
                    new Date(new Date().setHours(0, 0, 0, 0))
                  );
                  return (
                    <div key={invoice.id}>
                      <InvoiceContainer className="item-container">
                        <div className="attribute main-info-header">
                          <div className="titles">{titles.importer}</div>
                          <div className="main-info-header">
                            <p className="id">{consigneeRuc}</p>
                            <p>{importer}</p>
                            <VolanteId>{volanteId}</VolanteId>
                          </div>
                        </div>

                        <div className="attribute">
                          <div className="titles">{titles.importer}</div>
                          <StatusSectionDetails invoice={invoice} />
                        </div>
                        <div className="attribute">
                          <div className="titles">{titles.retirement}</div>
                          <ColoredTag
                            hasExpired={moment(withdrawalDate) < today}
                          >
                            {dateToRegularFormat(withdrawalDate)}
                          </ColoredTag>
                        </div>

                        <div className="attribute main-info-header">
                          <div className="titles">{titles.rucId}</div>
                          <div className="main-info-header">
                            <p className="ruc-id">{ruc.ruc}</p>
                            <p>{ruc.name}</p>
                          </div>
                        </div>

                        <div className="attribute">
                          <div className="titles">{"NRO PEDIDO"}</div>
                          <div>
                            {invoice.factura_num ? invoice.factura_num : "-"}
                          </div>
                        </div>

                        <div className="attribute">
                          <div className="titles">
                            {titles.electronicFacNum}
                          </div>
                          <div>{electronicFacNum ? electronicFacNum : "-"}</div>
                        </div>

                        <div className="attribute">
                          <div className="titles">{titles.amount}</div>
                          <div>{formatMoney(amount, useDollars)}</div>
                        </div>

                        <Authorization actions={["files.download_files"]}>
                          <div className="attribute">
                            <div className="titles">{titles.exportPdf}</div>
                            <ExportButton
                              disabled={!quotationDoc}
                              tooltipText={
                                quotationDoc
                                  ? "Descargar PDF"
                                  : "PDF no disponible"
                              }
                              onClick={() => {
                                handleDownload(quotationDoc);
                              }}
                            />
                          </div>
                        </Authorization>
                      </InvoiceContainer>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </InvoicesContainer>

      {loadInvoices.status === "SUCCESS" && (
        <>
          {loadInvoices.data.count > 0 && (
            <Container>
              <Authorization
                actions={["quotation.get_quotation_paid_intranet"]}
              >
                <div className="flex-paginator">
                  <Paginator
                    first={
                      (loadInvoices.params.page - 1) *
                      loadInvoices.data.page_size
                    }
                    rows={loadInvoices.data.page_size}
                    totalRecords={loadInvoices.data.count}
                    onPageChange={(e) => {
                      const targetPage = e.first / loadInvoices.data.page_size;
                      const pathname = history.location.pathname;
                      const parsedParams = parse(history.location.search);
                      const stringifiedParams = stringify({
                        ...parsedParams,
                        page: targetPage + 1,
                      });
                      history.push(`${pathname}?${stringifiedParams}`);
                    }}
                  />
                </div>
              </Authorization>
            </Container>
          )}
        </>
      )}
    </>
  );
};

function generateDownloadUrl(id) {
  return networkBackendRequest(
    "/files/presigned-download-url/",
    HTTP_METHODS.GET,
    {
      object_name: id,
    }
  )
    .then((response) => response.json())
    .then((response) => response.url);
}
